import { handleStoreState } from "Stores/RESTStore/CoreStore";
import { action, runInAction, makeObservable, observable, toJS, computed } from "mobx";
import { API } from "General/API";
import { API_URL } from "General/APIEndpoints";
import { projectsDataStore } from "Stores";
import { contactsStore } from "Stores/ContactsStore";
import { SubmitTopicRequest } from "Components/XKTViewer/Stores/BCFStore";
import { download } from "utils/download";
import { isEmpty, pick } from "lodash";
import { BcfServerFileUnited } from "../Components/BCFModels";
import { BCF_SERVER_EXTENSIONS_exportBcfData } from "General/APIQueryParams";
import Axios, { CancelTokenSource } from "axios";
import { getUid } from "utils/uid";
import { DefaultOptionType } from "antd/lib/select";

export type BCFIssuesFilters = Pick<
  API_BcfServerFilterRequest,
  "assigned_to" | "creation_date" | "labels" | "due_date" | "priority" | "stage" | "topic_status" | "topic_type"
>;

export const bcfIssuesFiltersKeys: Array<keyof BCFIssuesFilters> = [
  "assigned_to",
  "creation_date",
  "due_date",
  "labels",
  "priority",
  "stage",
  "topic_status",
  "topic_type",
];

export interface BCFIssuesTopicsWithSnapshots extends API_BcfServerTopic {
  snapshot: API_BcfServerSnapshot;
  topicDeleted: boolean;
}

export class BCFIssuesStore {
  data: BCFIssuesTopicsWithSnapshots[] = [];
  extensions: API_BcfServerProjectExtensions = {};
  projectUsers: DefaultOptionType[] = [];
  preservedFormData: string = "";
  sortOrder: API_BCFSERVERFILTERREQUEST_SORTORDER = "ASC";
  orderBy: keyof API_BcfServerTopic = "title";
  filters: BCFIssuesFilters = {};
  filesOfTopics: API_BcfServerIfcFileInfo[] = [];
  query: string = "";
  filterSettings: API_BcfServerFilterSetting[] = [];
  showDeleted: boolean = false;
  filtersFetched: boolean = false;
  fromSavedFilter: boolean = false;
  _cancelTokenSources: Map<string, CancelTokenSource> = new Map();

  constructor() {
    makeObservable(this, {
      init: action.bound,
      data: observable,
      extensions: observable,
      orderBy: observable,
      sortOrder: observable,
      filters: observable,
      fetchByQuery: action.bound,
      exportBCF: action.bound,
      createIssue: action.bound,
      filesOfTopics: observable,
      query: observable,
      getFilterSettings: action.bound,
      setFilterSettings: action.bound,
      filterSettings: observable,
      clear: action.bound,
      markAsDeleted: action.bound,
      showDeleted: observable,
      filtersFetched: observable,
      saveFiltersSettings: action.bound,
      deleteFiltersSettings: action.bound,
      editFiltersSettings: action.bound,
      setFavFilters: action.bound,
      fromSavedFilter: observable,
      assignedToOptions: computed,
      // setFiltersSettingsToDefault: action.bound,
    });
  }

  clear() {
    this.data = [];
    this.extensions = {};
    this.projectUsers = [];
    this.sortOrder = "ASC";
    this.orderBy = "title";
    this.filters = {};
    this.filesOfTopics = [];
    this.query = "";
    this.showDeleted = false;
    this.filtersFetched = false;
    this.fromSavedFilter = false;
    if (this._cancelTokenSources.size) {
      this._cancelTokenSources.forEach((source) => source?.cancel());
      this._cancelTokenSources.clear();
    }
  }

  @handleStoreState()
  async init(ignoreFilters?: boolean) {
    try {
      await this.getFilterSettings();
      await Promise.all([this.fetchByQuery(ignoreFilters), this.fetchProjectExtensions()]);
    } catch (error) {
      throw error;
    }
  }

  async fetchProjectExtensions() {
    const { token, clear } = this.registerCancelToken();
    try {
      const response = await API.getData<API_BcfServerProjectExtensions>(
        API_URL.BCF_SERVER_PROJECTS.bcfServerGetProjectExtensions(projectsDataStore.bcfGuid),
        null,
        { cancelToken: token }
      );
      runInAction(() => {
        this.extensions = response;
        this.projectUsers = this.buildProjectUsers(response);
      });
    } catch (error) {
      throw error;
    } finally {
      clear();
    }
  }

  private buildProjectUsers(response: API_BcfServerProjectExtensions): DefaultOptionType[] {
    if (response.userIds?.length) {
      return response.userIds.map((userId) => ({
        label: contactsStore.getUserNameById(userId),
        value: userId,
      }));
    }
    if (response.topicAssignments?.length) {
      return response.topicAssignments
        .filter((item) => item.id != null)
        .map((item) => ({
          label: contactsStore.getUserNameById(item.id, false) || item.name,
          value: item.id,
        }));
    }
    return contactsStore.users.map((user) => ({
      label: contactsStore.getUserNameById(user.id),
      value: user.id,
    }));
  }

  private mapColumnKeyToOrderBy(columnKey: keyof API_BcfServerTopic): API_BCFSERVERFILTERREQUEST_ORDERBY {
    if (columnKey === "assigned_to_id") {
      return "ASSIGNED_TO";
    }
    return columnKey.toUpperCase() as API_BCFSERVERFILTERREQUEST_ORDERBY;
  }

  private mapOrderByToColumnKey(orderBy: API_BCFSERVERFILTERREQUEST_ORDERBY): keyof API_BcfServerTopic {
    if (orderBy === "ASSIGNED_TO") {
      return "assigned_to_id";
    }
    return orderBy.toLowerCase() as keyof API_BcfServerTopic;
  }

  async createIssue({ viewpoints, ...topic }: SubmitTopicRequest) {
    try {
      const topicResponse = await API.newData<API_BcfServerTopic>(
        API_URL.BCF_SERVER_TOPICS.bcfServerPostTopic(projectsDataStore.bcfGuid),
        topic
      );
      if (viewpoints) {
        await Promise.all(
          viewpoints.map((viewpoint) =>
            API.newData(
              API_URL.BCF_SERVER_VIEWPOINTS.bcfServerPostViewpoint(projectsDataStore.bcfGuid, topicResponse.guid),
              viewpoint
            )
          )
        );
      }
      this.init();
    } catch (error) {
      throw error;
    }
  }

  async updateTopic(topicId: string, topic: API_BcfServerTopic) {
    try {
      await API.editData<API_BcfServerTopic>(
        API_URL.BCF_SERVER_TOPICS.bcfServerPutTopicById(projectsDataStore.bcfGuid, topicId),
        topic
      );
      this.init();
    } catch (error) {
      throw error;
    }
  }

  async bulkEditTopics(topicGuids: string[], updatedTopic: API_BcfServerTopic) {
    try {
      const body: API_BcfServerTopicsMultiEdit = { topicGuids, updatedTopic };
      await API.editData<API_BcfServerTopic>(
        API_URL.BCF_SERVER_EXTENSIONS.bcfServerMultiUpdateTopics(projectsDataStore.projectId),
        body
      );
      this.init();
    } catch (error) {
      throw error;
    }
  }

  async fetchByQuery(ignoreFilters?: boolean, fromSavedFilter?: boolean) {
    const { token, clear } = this.registerCancelToken();
    try {
      const showViewpoints = this.filterSettings.find((item) => item.filterName === "DEFAULT")?.showViewpoint;
      const body: API_BcfServerFilterRequest = {
        ...toJS(ignoreFilters ? {} : this.filters),
        orderBy: this.mapColumnKeyToOrderBy(this.orderBy),
        sortOrder: this.sortOrder,
        ...(this.query && { searchQuery: this.query }),
        showDeleted: this.showDeleted,
        authorization: true,
        showViewpoints: showViewpoints,
      };

      if (body.assigned_to_id?.length) {
        const { integers, strings } = body.assigned_to_id.reduce(
          (acc, item) => {
            // in ScrollableTable we use string[] for assigned_to_id, but in API we use number[]
            if (Number.isNaN(Number.parseInt(item as unknown as string))) {
              acc.strings.push(item);
            } else {
              acc.integers.push(+item);
            }
            return acc;
          },
          { integers: [], strings: [] }
        );
        body.assigned_to_id = integers;
        if (strings.length) {
          body.assigned_to = strings;
        }
      }

      const response: API_BcfServerTopicWithSnapshot[] = await API.newData(
        API_URL.BCF_SERVER_TOPICS_FILTER.bcfServerFilterTopics(projectsDataStore.bcfGuid),
        body,
        null,
        { cancelToken: token }
      );
      // merge snapshot and topic into one object
      const newResponse: BCFIssuesTopicsWithSnapshots[] = response?.map((item: API_BcfServerTopicWithSnapshot) => ({
        ...item.topic,
        snapshot: item.snapshot,
        topicDeleted: item.topicDeleted,
      }));

      runInAction(() => {
        this.data = newResponse;
        this.fromSavedFilter = !!fromSavedFilter;
      });
    } catch (error) {
      throw error;
    } finally {
      clear();
    }
  }

  setSorter(orderBy, sortOrder: "ASC" | "DESC") {
    runInAction(() => {
      this.orderBy = orderBy;
      this.sortOrder = sortOrder;
    });
  }

  setFilters(currentFilters: BCFIssuesFilters) {
    runInAction(() => {
      this.filters = currentFilters;
    });
  }
  setFavFilters(currentFilters: API_BcfServerFilterRequest) {
    const favFilters = pick(currentFilters, bcfIssuesFiltersKeys);
    runInAction(() => {
      this.filters = favFilters;
      this.orderBy = this.mapOrderByToColumnKey(currentFilters.orderBy);
      this.sortOrder = currentFilters?.sortOrder;
    });
  }

  setQuery(value) {
    runInAction(() => {
      this.query = value;
    });
  }

  async exportBCF(bcfVersion: API_BCFEXPORTREQUEST_BCFVERSION, withDeleted?: boolean, keys?: string[]) {
    try {
      const body: API_BcfExportRequest = {
        topicGuds: !isEmpty(keys) ? keys : this.data.map((item) => item.guid),
        bcfVersion,
      };
      const params: BCF_SERVER_EXTENSIONS_exportBcfData = {
        withDeleted,
      };
      const bcfFile = await API.getBlobByPost(
        API_URL.BCF_SERVER_EXTENSIONS.exportBcfData(projectsDataStore.projectId),
        body,
        params
      );
      download(bcfFile, bcfFile.name);
    } catch (error) {
      throw error;
    }
  }

  async fetchFilesOfTopic(topicGuid: string) {
    try {
      const response = await API.getData<API_BcfServerIfcFileInfo[]>(
        API_URL.BCF_SERVER_EXTENSIONS.bcfServerGetTopicFileInfo(projectsDataStore.projectId, topicGuid)
      );
      this.filesOfTopics = response;
      return response;
    } catch (error) {
      throw error;
    }
  }

  async fetchTopicAttachments(topicGuid: string) {
    try {
      const response = await API.getData<API_BcfServerIfcFileInfo[]>(
        API_URL.BCF_SERVER_EXTENSIONS.bcfServerGetAttachments(projectsDataStore.projectId, topicGuid)
      );
      return response;
    } catch (error) {
      throw error;
    }
  }

  async addFiles(topicGuid: string, fileIds: number[]) {
    try {
      await API.newData<API_BcfServerIfcFileInfo[]>(
        API_URL.BCF_SERVER_EXTENSIONS.bcfServerAddProjectFile(projectsDataStore.projectId, topicGuid),
        {
          fileIds,
        }
      );
    } catch (error) {
      throw error;
    }
  }

  async removeFiles(topicGuid: string, fileIds: number[]) {
    try {
      const body = {
        fileIds: fileIds,
      };
      await API.deleteData(
        API_URL.BCF_SERVER_EXTENSIONS.bcfServerRemoveAttachments(projectsDataStore.projectId, topicGuid),
        null,
        { body }
      );
    } catch (error) {
      throw error;
    }
  }

  async fetchModels(topicGuid: string) {
    try {
      const response = await API.getData<API_BcfServerFile[]>(
        API_URL.BCF_SERVER_FILES.bcfServerGetTopicFiles(projectsDataStore.bcfGuid, topicGuid)
      );
      return response;
    } catch (error) {
      throw error;
    }
  }

  async addModels(topicGuid: string, files: API_FileAttachment[]) {
    try {
      const responses = await Promise.all(
        files.map((item) =>
          API.newData<API_BcfServerFile>(
            API_URL.BCF_SERVER_EXTENSIONS.bcfServerAddIfcModelToTopic(projectsDataStore.projectId, topicGuid, item.id),
            {}
          )
        )
      );
      return responses.filter((response) => response?.reference).length;
    } catch (error) {
      throw error;
    }
  }

  async deleteModels(topicGuid: string, files: BcfServerFileUnited[]) {
    try {
      await Promise.all(
        files.map((item) => {
          const body: API_BcfServerFile = {
            date: item.date,
            filename: item.fileName,
            reference: item.reference,
            ifc_project: item.ifc_project,
            ifc_spatial_structure_element: item.ifc_spatial_structure_element,
          };
          return API.deleteData(
            API_URL.BCF_SERVER_EXTENSIONS.bcfServerRemoveProjectFile(projectsDataStore.projectId, topicGuid),
            null,
            { body }
          );
        })
      );
    } catch (error) {
      throw error;
    }
  }

  async overrideModelsForTopics(topicGuids: string[], files: API_FileAttachment[]) {
    const results = await Promise.allSettled(topicGuids.map((topicGuid) => this.setTopicModels(topicGuid, files)));
    const failedTopicGuids = topicGuids.filter((_, index) => results[index].status === "rejected");
    if (failedTopicGuids.length) {
      throw new Error(`Failed to update models for topics: ${failedTopicGuids.join(", ")}`);
    }
  }

  private async setTopicModels(topicGuid: string, files: API_FileAttachment[]) {
    const [currentFiles, fileInfos] = await Promise.all([
      API.getData<API_BcfServerFile[]>(
        API_URL.BCF_SERVER_FILES.bcfServerGetTopicFiles(projectsDataStore.bcfGuid, topicGuid)
      ),
      API.getData<API_BcfServerIfcFileInfo[]>(
        API_URL.BCF_SERVER_EXTENSIONS.bcfServerGetTopicFileInfo(projectsDataStore.projectId, topicGuid)
      ),
    ]);

    const desiredFileIds = new Set(files.map((file) => file.id));
    const linkedFileIds = new Set((fileInfos ?? []).map((info) => info.id));
    const fileIdByReference = new Map((fileInfos ?? []).map((info) => [info.modelReference, info.id]));

    const filesToAdd = files.filter((file) => !linkedFileIds.has(file.id));
    const filesToRemove = (currentFiles ?? []).filter((file) => {
      const linkedFileId = fileIdByReference.get(file.reference);
      return linkedFileId === undefined || !desiredFileIds.has(linkedFileId);
    });

    // Add first so a failing request never leaves a topic with fewer models than before.
    await Promise.all(
      filesToAdd.map((file) =>
        API.newData<API_BcfServerIfcFileInfo[]>(
          API_URL.BCF_SERVER_EXTENSIONS.bcfServerAddIfcModelToTopic(projectsDataStore.projectId, topicGuid, file.id),
          {}
        )
      )
    );
    await Promise.all(
      filesToRemove.map((file) =>
        API.deleteData(
          API_URL.BCF_SERVER_EXTENSIONS.bcfServerRemoveProjectFile(projectsDataStore.projectId, topicGuid),
          null,
          { body: file }
        )
      )
    );
  }

  async relinkModel(topicGuid: string, targetFile: BcfServerFileUnited, fileId: number) {
    const previousReference = targetFile.reference;
    const body: API_BcfServerFile = {
      date: targetFile.date,
      filename: targetFile.fileName,
      reference: targetFile.reference,
      ifc_project: targetFile.ifc_project,
      ifc_spatial_structure_element: targetFile.ifc_spatial_structure_element,
    };
    try {
      const response = await API.editData<API_BcfServerFile>(
        API_URL.BCF_SERVER_EXTENSIONS.bcfServerRelinkTopicFile(projectsDataStore.projectId, topicGuid, fileId),
        body
      );
      return !!response?.reference && response.reference !== previousReference;
    } catch (error) {
      throw error;
    }
  }

  registerCancelToken() {
    const source = Axios.CancelToken.source();
    const uid = getUid();
    this._cancelTokenSources.set(uid, source);

    return { token: source.token, clear: () => this._cancelTokenSources.delete(uid) };
  }

  async getFilterSettings() {
    const { token, clear } = this.registerCancelToken();
    try {
      const response = await API.getData(
        API_URL.BCF_SERVER_TOPICS_FILTER.bcfServerGetFilterSettings(projectsDataStore.bcfGuid),
        null,
        { cancelToken: token }
      );
      runInAction(() => {
        this.filterSettings = response;
      });
    } catch (error) {
      throw error;
    } finally {
      runInAction(() => {
        this.filtersFetched = true;
      });
      clear();
    }
  }

  async setFilterSettings(settings: API_BcfServerFilterSetting) {
    try {
      const defaultFilterSettings = this.filterSettings.find((item) => item.filterName === "DEFAULT");
      const body: API_BcfServerFilterSetting = {
        ...settings,
        // apply selected settings to DEFAULT settings
        id: defaultFilterSettings.id,
        filterName: defaultFilterSettings.filterName,
      };
      const response: API_BcfServerFilterSetting[] = await API.editData(
        API_URL.BCF_SERVER_TOPICS_FILTER.bcfServerPutFilterSetting(projectsDataStore.bcfGuid),
        body
      );
      runInAction(() => {
        this.showDeleted = settings.filteringData?.showDeleted;
        this.filterSettings = response;
      });
    } catch (error) {
      throw error;
    }
  }

  async saveFiltersSettings(filterName: string) {
    try {
      const defaultFilterSettings = structuredClone(this.filterSettings.find((item) => item.filterName === "DEFAULT"));
      delete defaultFilterSettings.id;
      delete defaultFilterSettings.projectId;
      const filteringData: API_BcfServerFilterRequest = {
        ...toJS(this.filters),
        orderBy: this.mapColumnKeyToOrderBy(this.orderBy),
        sortOrder: this.sortOrder,
        ...(this.query && { searchQuery: this.query }),
        showDeleted: this.showDeleted,
        authorization: true,
      };
      const body: API_BcfServerFilterSetting = {
        ...defaultFilterSettings,
        filterName,
        filteringData,
      };

      const response: API_BcfServerFilterSetting[] = await API.newData(
        API_URL.BCF_SERVER_TOPICS_FILTER.bcfServerPostFilterSetting(projectsDataStore.bcfGuid),
        body
      );
      runInAction(() => {
        this.filterSettings = response;
      });
    } catch (error) {
      throw error;
    }
  }

  async editFiltersSettings(id: number, filterName: string) {
    try {
      const selectedSettings = this.filterSettings.find((item) => item.id === id);
      const body: API_BcfServerFilterSetting = {
        ...selectedSettings,
        filterName,
      };
      const response: API_BcfServerFilterSetting[] = await API.editData(
        API_URL.BCF_SERVER_TOPICS_FILTER.bcfServerPutFilterSetting(projectsDataStore.bcfGuid),
        body
      );
      runInAction(() => {
        this.filterSettings = response;
      });
    } catch (error) {
      throw error;
    }
  }

  async deleteFiltersSettings(id: number) {
    try {
      const response = await API.deleteData(
        API_URL.BCF_SERVER_TOPICS_FILTER.bcfServerDeleteFilterSetting(projectsDataStore.bcfGuid, id)
      );
      runInAction(() => {
        this.filterSettings = response;
      });
    } catch (error) {
      throw error;
    }
  }

  async markAsDeleted(topicsId: string[]) {
    try {
      const formBody: API_BcfServerTopicsMultiDelete = {
        topicGuids: topicsId,
      };
      await API.deleteData(API_URL.BCF_SERVER_EXTENSIONS.bcfServerDeleteTopics(projectsDataStore.projectId), null, {
        body: formBody,
      });
    } catch (error) {
      throw error;
    }
  }

  async restoreTopic(topicsId: string[]) {
    try {
      const formBody: API_BcfServerTopicsMultiDelete = {
        topicGuids: topicsId,
      };
      await API.newData(API_URL.BCF_SERVER_EXTENSIONS.bcfServerRestoreTopics(projectsDataStore.projectId), formBody);
    } catch (error) {
      throw error;
    }
  }

  changeShowDeleted() {
    runInAction(() => {
      this.showDeleted = !this.showDeleted;
      this.filters = {};
    });
  }

  /**
   * Maps all topics to get distinct assigned to options. If no user found uses assigned_to as fallback.
   * @returns Array of options with value and text.
   */
  get assignedToOptions() {
    return (
      this.extensions?.topicAssignments?.map((item, idx) => ({
        value: item.id === null ? item.name : String(item.id),
        label: contactsStore.getUserNameById(Number(item.id), false) || item.name,
        key: idx,
      })) || []
    );
  }
}

export const bcfIssuesStore = new BCFIssuesStore();
